import {
  type Dot,
  type HillChart,
  type HillChartListResponse,
  type SnapshotsListResponse,
  type WsEvent,
  wsEventSchema,
} from '@hillchart/shared';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { API_URL } from './api';
import { queryKeys } from './query-keys';

/**
 * Subscribe to realtime events for a hill chart and apply them to the
 * TanStack Query cache.
 *
 * Lifecycle:
 *  - Opens `GET /api/hills/:id/ws` with cookie credentials.
 *  - Parses every server frame through `wsEventSchema` - malformed events
 *    are silently dropped so a single bad payload can't poison the socket.
 *  - On unexpected close, reconnects with an exponential backoff
 *    (1s -> 2s -> 4s -> 8s -> capped at 16s). A clean `close(1000)` on
 *    unmount skips reconnection.
 *  - Terminal close codes - the server told us there's no point retrying:
 *      4400 missing id          (route param somehow empty)
 *      4404 hill not found      (deleted or never existed)
 *      4410 hill deleted        (deleted while we were connected)
 *      4423 hill completed      (marked Done while we were connected;
 *                                live mode is over for this hill)
 *
 * Cache reconciliation:
 *  - `dot:updated` / `dot:added` / `dot:removed` patch the hill detail
 *    cache in place so the UI reflects the change without a refetch.
 *  - `dot:updated` applies a last-write-wins guard via `lastMovedAt`:
 *    a stale event (older timestamp than the local row) is dropped so
 *    a late echo of my own optimistic update can't regress a newer move
 *    by another user.
 *  - `snapshot:created` prepends to the snapshots list cache (newest
 *    first matches the list endpoint's ordering).
 *  - `hill:completed` patches the detail cache with the lifecycle fields
 *    and invalidates every hill-list cache (active + done) so the home
 *    drops the hill and the archive picks it up.
 *  - `hill:orphaned` patches the detail cache with `externalProjectDeletedAt`
 *    so the orphan banner can render without a refetch.
 *  - `hill:renamed` patches the user-owned `title` in the detail cache and
 *    in the list cache, so both the header and the card grid follow a
 *    rename made by another client.
 *  - `hill:deleted` evicts the hill detail + snapshots caches and
 *    invalidates the lists so any stale card is dropped on next render.
 *
 * We intentionally do not invalidate queries on dot events - patching the
 * cache avoids extra roundtrips and keeps the UI in sync with optimistic
 * drag mutations without racing them. Lifecycle events DO invalidate
 * because they cross a list boundary (active <-> done) that a single
 * setQueryData cannot express.
 */
export type UseHillSocketStatus = 'idle' | 'connecting' | 'open' | 'closed';

const MAX_BACKOFF_MS = 16_000;

/**
 * Close codes the server uses for terminal states - the client must not
 * reconnect after seeing any of these. Kept here (not in shared) because
 * close codes are a transport-level contract between this hook and the
 * Hono ws route, not a domain shape the rest of the app cares about.
 */
const TERMINAL_CLOSE_CODES = new Set<number>([4400, 4404, 4410, 4423]);

function buildWsUrl(hillChartId: string): string {
  // Swap http/https for ws/wss while preserving host:port from the API URL.
  // In the prod Docker image VITE_API_URL is baked as "" so fetch calls fall
  // back to relative paths (Caddy routes /api/* on the same origin). But
  // `new URL("")` throws - we need an absolute base to parse. Fall back to
  // the document's own origin, which is exactly the host Caddy serves us
  // from, so the WebSocket ends up on the right host whether the bundle
  // ships an absolute VITE_API_URL (split-origin dev) or an empty one
  // (same-origin prod).
  const base = API_URL || window.location.origin;
  const apiUrl = new URL(base);
  const protocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${apiUrl.host}/api/hills/${hillChartId}/ws`;
}

export function useHillSocket(hillChartId: string | null): {
  status: UseHillSocketStatus;
} {
  const qc = useQueryClient();
  const [status, setStatus] = useState<UseHillSocketStatus>('idle');

  useEffect(() => {
    if (!hillChartId) {
      setStatus('idle');
      return;
    }

    let ws: WebSocket | null = null;
    let reconnectTimer: number | null = null;
    let disposed = false;
    let retryCount = 0;

    const connect = (): void => {
      if (disposed) return;
      setStatus('connecting');
      ws = new WebSocket(buildWsUrl(hillChartId));

      ws.onopen = () => {
        retryCount = 0;
        setStatus('open');
      };

      ws.onmessage = (msg) => {
        let event: WsEvent;
        try {
          const parsed = JSON.parse(String(msg.data));
          event = wsEventSchema.parse(parsed);
        } catch {
          // Drop malformed frames silently - one bad message must not
          // tear down the socket for the rest.
          return;
        }
        applyEvent(qc, event);
      };

      ws.onclose = (evt) => {
        setStatus('closed');
        ws = null;
        if (disposed) return;

        // App-defined terminal codes: the server told us there's no point
        // reconnecting (hill is gone, completed, or never existed). Treat
        // anything else as transient and retry with exponential backoff.
        if (TERMINAL_CLOSE_CODES.has(evt.code)) return;

        const delay = Math.min(1000 * 2 ** retryCount, MAX_BACKOFF_MS);
        retryCount += 1;
        reconnectTimer = window.setTimeout(connect, delay);
      };

      ws.onerror = () => {
        // Don't act here - `onclose` follows `onerror` and owns cleanup.
      };
    };

    connect();

    return () => {
      disposed = true;
      if (reconnectTimer != null) window.clearTimeout(reconnectTimer);
      // 1000 = normal closure; prevents reconnect on the other end too.
      ws?.close(1000, 'component unmounted');
    };
  }, [hillChartId, qc]);

  return { status };
}

/**
 * Mutate the matching hill inside the hills-list cache, if that cache is
 * populated. Mirrors the detail-cache patches so the card thumbnails on
 * `/hills` stay in sync without forcing a refetch (the list query has a
 * 30s staleTime - without this, a WS event would sit invisibly until the
 * user explicitly refreshed the list).
 *
 * A no-op when the list query has never been fetched (`prev === undefined`);
 * TanStack Query will fetch fresh on its first observer anyway.
 */
function mutateHillInListCache(
  qc: QueryClient,
  hillChartId: string,
  mutate: (hill: HillChart) => HillChart,
): void {
  qc.setQueryData<HillChartListResponse>(queryKeys.hillsList(), (prev) => {
    if (prev === undefined) return prev;
    return {
      ...prev,
      hills: prev.hills.map((h) => (h.id === hillChartId ? mutate(h) : h)),
    };
  });
}

/** Shared last-write-wins guard - see comment at the first use below. */
function isStaleDotUpdate(existing: Dot | undefined, incoming: Dot): boolean {
  return (
    existing?.lastMovedAt !== undefined &&
    existing.lastMovedAt !== null &&
    incoming.lastMovedAt !== null &&
    incoming.lastMovedAt < existing.lastMovedAt
  );
}

/**
 * Dispatch a single WS event into the query cache. Exported for testing.
 */
export function applyEvent(qc: QueryClient, event: WsEvent): void {
  switch (event.type) {
    case 'dot:updated': {
      // Decide once, against the detail cache, whether the event is stale.
      // Applying the same decision to both caches guarantees they never
      // drift - if we skipped the detail patch, we must skip the list
      // patch too, otherwise the list thumbnail would briefly reflect a
      // value the detail view rejected.
      const detailPrev = qc.getQueryData<HillChart>(queryKeys.hill(event.hillChartId));
      const existing = detailPrev?.dots.find((d) => d.id === event.dot.id);
      // Last-write-wins via ISO-8601 string comparison: if the event is
      // stale relative to what we already know, drop it. Prevents a late
      // echo of an earlier PATCH from clobbering a newer move another
      // client pushed while this event was in flight.
      if (isStaleDotUpdate(existing, event.dot)) {
        return;
      }

      qc.setQueryData<HillChart | undefined>(queryKeys.hill(event.hillChartId), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          dots: prev.dots.map((d) => (d.id === event.dot.id ? event.dot : d)),
        };
      });
      mutateHillInListCache(qc, event.hillChartId, (h) => ({
        ...h,
        dots: h.dots.map((d) => (d.id === event.dot.id ? event.dot : d)),
      }));
      return;
    }
    case 'dot:added': {
      qc.setQueryData<HillChart | undefined>(queryKeys.hill(event.hillChartId), (prev) => {
        if (!prev) return prev;
        if (prev.dots.some((d) => d.id === event.dot.id)) return prev;
        return { ...prev, dots: [...prev.dots, event.dot] };
      });
      mutateHillInListCache(qc, event.hillChartId, (h) =>
        h.dots.some((d) => d.id === event.dot.id) ? h : { ...h, dots: [...h.dots, event.dot] },
      );
      return;
    }
    case 'dot:removed': {
      qc.setQueryData<HillChart | undefined>(queryKeys.hill(event.hillChartId), (prev) => {
        if (!prev) return prev;
        return { ...prev, dots: prev.dots.filter((d) => d.id !== event.dotId) };
      });
      mutateHillInListCache(qc, event.hillChartId, (h) => ({
        ...h,
        dots: h.dots.filter((d) => d.id !== event.dotId),
      }));
      return;
    }
    case 'snapshot:created': {
      qc.setQueryData<SnapshotsListResponse | undefined>(
        queryKeys.snapshots(event.hillChartId),
        (prev) => {
          if (!prev) return { snapshots: [event.snapshot] };
          if (prev.snapshots.some((s) => s.id === event.snapshot.id)) return prev;
          return { snapshots: [event.snapshot, ...prev.snapshots] };
        },
      );
      return;
    }
    case 'hill:completed': {
      // Detail cache: stamp the lifecycle fields so the page re-renders in
      // its read-only branch (Mark as Done button hidden, etc).
      qc.setQueryData<HillChart | undefined>(queryKeys.hill(event.hillChartId), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          completedAt: event.completedAt,
          completedByUserId: event.completedByUserId,
        };
      });
      // List caches: the hill leaves the active list and joins the done
      // list. We invalidate the shared prefix so both refetch on next
      // observer access. A direct setQueryData would have to mutate two
      // separate caches and risk drift; invalidation is the simpler path.
      qc.invalidateQueries({ queryKey: queryKeys.hillsListPrefix() });
      return;
    }
    case 'hill:orphaned': {
      // The GitLab project backing this hill has been deleted upstream.
      // We do not delete the hill - the user must manually mark Done or
      // delete it. The detail page renders a warning banner from this
      // flag, so patching the cache is enough.
      qc.setQueryData<HillChart | undefined>(queryKeys.hill(event.hillChartId), (prev) => {
        if (!prev) return prev;
        return { ...prev, externalProjectDeletedAt: event.externalProjectDeletedAt };
      });
      return;
    }
    case 'hill:renamed': {
      // The title is user-owned - resync never writes it - so this event is
      // the only way a client that did not perform the rename learns about
      // it. Both caches carry a title, so both are patched: without the
      // list patch the card grid on /hills would keep the old name until
      // its 30s staleTime lapsed.
      //
      // `externalProjectName` is deliberately untouched: it keeps mirroring
      // the upstream project, and the two diverging is the point of the
      // feature.
      qc.setQueryData<HillChart | undefined>(queryKeys.hill(event.hillChartId), (prev) => {
        if (!prev) return prev;
        return { ...prev, title: event.title };
      });
      mutateHillInListCache(qc, event.hillChartId, (h) => ({ ...h, title: event.title }));
      return;
    }
    case 'hill:deleted': {
      // Hard delete: drop every cached query for this hill so a stale
      // detail render cannot show a "not found" mid-state, then invalidate
      // the lists so any populated card is dropped on next refetch. The
      // companion close frame (4410) tears down the WS room on the server
      // side, so the client's reconnect logic will see a terminal code
      // and stop trying.
      qc.removeQueries({ queryKey: queryKeys.hill(event.hillChartId) });
      qc.removeQueries({ queryKey: queryKeys.snapshots(event.hillChartId) });
      qc.invalidateQueries({ queryKey: queryKeys.hillsListPrefix() });
      return;
    }
  }
}
